import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
    taskChargesHistoryByPackageIdService,
    taskChargesHistoryByProductIdService,
} from '../../../services/company.service';
import LoaderSpinner from '../../common/Loader';
import PostChargeOnTaskModal from './PostChargeOnTaskModal';

/* istanbul ignore next */
const formatChargeAmount = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '$0.00';
    if (typeof raw === 'number') return `$${raw.toFixed(2)}`;
    const cleaned = String(raw).replaceAll(/[^0-9.-]/g, '');
    const num = Number(cleaned);
    return Number.isNaN(num) ? `$${String(raw)}` : `$${num.toFixed(2)}`;
};

/* istanbul ignore next */
const formatChargeDate = (c) => {
    if (c.postChargeDate) return moment(c.postChargeDate).format('MM-DD-YYYY');
    if (c.entryDate) return moment(c.entryDate).format('MM-DD-YYYY');
    if (c.createdDate) return moment(c.createdDate).format('MM-DD-YYYY');
    return '-';
};

/* istanbul ignore next */
const formatInvoiceDate = (invoice) => {
    const invoiceDate = invoice.InvoiceDate || invoice.invoiceDate;
    return invoiceDate ? moment(invoiceDate).format('MM-DD-YYYY') : '-';
};

/* istanbul ignore next */
const getInvoiceNumber = (invoice) => (
    invoice.InvoiceNumber || invoice.invoiceNumber || '-'
);

/* istanbul ignore next */
const getInvoiceTrackingNo = (invoice) => (
    invoice.TrackingNo || invoice.trackingNo || '-'
);

/* istanbul ignore next */
const getInvoiceAmount = (invoice) => (
    invoice.Amount ?? invoice.amount
);

/* istanbul ignore next */
const TasksChargesHistoryModal = ({ show, handleCancel, productContext, focusSection }) => {
    const [loader, setLoader] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [charges, setCharges] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [chargeTask, setChargeTask] = useState(null);
    const invoicesSectionRef = useRef(null);
    const isSuperAdmin = useSelector((state) => state?.auth?.user?.Value?.isSuperAdmin);

    const loadHistory = useCallback(async () => {
        const referenceId = productContext?.referenceId;
        const referenceType = productContext?.referenceType;
        if (!referenceId || !referenceType) {
            setTasks([]);
            setCharges([]);
            setInvoices([]);
            return;
        }
        setLoader(true);
        try {
            let res;
            if (referenceType === 'Package') {
                res = await taskChargesHistoryByPackageIdService({ packageId: referenceId });
            } else {
                res = await taskChargesHistoryByProductIdService({ productId: referenceId });
            }
            const entity = res?.data?.Entity || {};
            setTasks(entity.taskList || []);
            setCharges(entity.postCharges || []);
            setInvoices(entity.invoiceList || entity.invoices || entity.invoiceDetails || []);
        } catch (e) {
            console.error(e);
            setTasks([]);
            setCharges([]);
            setInvoices([]);
        } finally {
            setLoader(false);
        }
    }, [productContext?.referenceId, productContext?.referenceType]);

    useEffect(() => {
        if (!show) return;
        loadHistory();
    }, [show, loadHistory]);

    useEffect(() => {
        if (!show || focusSection !== 'invoices' || loader) return;
        invoicesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [show, focusSection, loader, invoices.length]);

    const renderHeader = () => {
        const parts = [];
        if (productContext?.sku) parts.push(`SKU: ${productContext.sku}`);
        if (productContext?.productName) parts.push(productContext.productName);
        if (productContext?.sellerName) parts.push(productContext.sellerName);
        return parts.join('  \u00B7  ');
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static" size="xl">
            <Modal.Header>
                <div>
                    <Modal.Title>Tasks &amp; Charges History</Modal.Title>
                    {renderHeader() && (
                        <div className="text-muted small mt-1">{renderHeader()}</div>
                    )}
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    className="btn btn-link p-0 border-0"
                    style={{ position: 'absolute', top: 12, right: 16, fontSize: 20, color: '#6c757d' }}
                    onClick={handleCancel}
                >
                    <i className="fa fa-times" />
                </button>
            </Modal.Header>
            <Modal.Body>
                {loader && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}

                <div className="mb-4">
                    <h6 className="text-primary mb-2">
                        <i className="fa fa-clipboard-list mr-2" /> TASKS CREATED
                    </h6>
                    <div className="tableFixHead incomingTbl" style={{ height: 'auto', maxHeight: '40vh' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Task ID</th>
                                    <th>Title</th>
                                    <th>Type</th>
                                    <th>Priority</th>
                                    <th>Created On</th>
                                    <th>Status</th>
                                    {isSuperAdmin && <th>Action</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.length === 0 ? (
                                    <tr>
                                        <td colSpan={isSuperAdmin ? 8 : 7} className="text-center text-muted">
                                            No tasks created yet. Use &quot;Add Task&quot; to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    tasks.map((t, idx) => (
                                        <tr key={t.taskId}>
                                            <td>{idx + 1}</td>
                                            <td>{t.taskId}</td>
                                            <td>{t.taskTitle}</td>
                                            <td>{t.taskType || '-'}</td>
                                            <td>{t.taskPriority || '-'}</td>
                                            <td>
                                                {t.createdDate
                                                    ? moment(t.createdDate).format(
                                                          'MM-DD-YYYY hh:mm A'
                                                      )
                                                    : '-'}
                                            </td>
                                            <td>{t.taskStatus || '-'}</td>
                                            {isSuperAdmin && (
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn cus-primary-bg-btn btn-sm"
                                                        onClick={() => setChargeTask(t)}
                                                    >
                                                        <i className="fa fa-plus" /> Charge
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div>
                    <h6 className="text-success mb-2">
                        <i className="fa fa-dollar-sign mr-2" /> CHARGES POSTED
                    </h6>
                    <div className="tableFixHead incomingTbl" style={{ height: 'auto', maxHeight: '40vh' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Linked Task</th>
                                    <th>Standalone</th>
                                    <th>Amount</th>
                                    <th>Applied On</th>
                                    <th>Comment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {charges.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted">
                                            No charges posted yet.
                                        </td>
                                    </tr>
                                ) : (
                                    charges.map((c, idx) => {
                                        let linkedTaskLabel = '\u2014';
                                        if (c.taskId) {
                                            const titleSuffix = c.taskTitle ? ` \u2014 ${c.taskTitle}` : '';
                                            linkedTaskLabel = `#${c.taskId}${titleSuffix}`;
                                        }
                                        return (
                                            <tr key={`charge-${idx}-${c.taskId || 'sa'}`}>
                                                <td>{idx + 1}</td>
                                                <td>{linkedTaskLabel}</td>
                                                <td>{c.isStandAlone ? 'Yes' : 'No'}</td>
                                                <td>{formatChargeAmount(c.postChargeAmount)}</td>
                                                <td>{formatChargeDate(c)}</td>
                                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 280 }}>
                                                    {c.postChargeComment || '-'}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div ref={invoicesSectionRef} className="mt-4">
                    <h6 className="text-info mb-2">
                        <i className="fa fa-file-invoice-dollar mr-2" /> INVOICES
                    </h6>
                    <div className="tableFixHead incomingTbl" style={{ height: 'auto', maxHeight: '40vh', overflowX: 'auto' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Invoice Number</th>
                                    <th>Tracking Number</th>
                                    <th>Invoice Date</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted">
                                            No invoices generated yet.
                                        </td>
                                    </tr>
                                ) : (
                                    invoices.map((invoice, idx) => (
                                        <tr key={`invoice-${invoice.InvoiceId || invoice.TransactionId || getInvoiceNumber(invoice)}`}>
                                            <td>{idx + 1}</td>
                                            <td>{getInvoiceNumber(invoice)}</td>
                                            <td>{getInvoiceTrackingNo(invoice)}</td>
                                            <td>{formatInvoiceDate(invoice)}</td>
                                            <td>{formatChargeAmount(getInvoiceAmount(invoice))}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn cus-primary-transparent-btn"
                    onClick={handleCancel}
                >
                    Close
                </button>
            </Modal.Footer>
            {chargeTask && (
                <div
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: 'inherit',
                        zIndex: 1040,
                        pointerEvents: 'auto',
                    }}
                />
            )}
            <PostChargeOnTaskModal
                show={!!chargeTask}
                handleCancel={() => setChargeTask(null)}
                task={chargeTask}
                productContext={productContext}
                onPosted={() => {
                    setChargeTask(null);
                    loadHistory();
                }}
            />
        </Modal>
    );
};

export default TasksChargesHistoryModal;

TasksChargesHistoryModal.propTypes = {
    show: PropTypes.bool,
    handleCancel: PropTypes.func,
    productContext: PropTypes.object,
    focusSection: PropTypes.string,
};
